import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MachineCard } from './MachineCard';
import { type Machine } from '../services/BackendDataService';
import { styles, cx } from '../styles';
import { useThemeClasses } from '../providers/ThemeProvider';

interface LazyMachineGridProps {
  machines: Machine[];
  onMachineClick: (machine: Machine) => void;
  initialLoadCount?: number;
  loadMoreCount?: number;
  className?: string;
}

const LazyMachineGrid: React.FC<LazyMachineGridProps> = React.memo(({
  machines,
  onMachineClick,
  initialLoadCount = 20,
  loadMoreCount = 20,
  className = ""
}) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();
  const [visibleCount, setVisibleCount] = useState(initialLoadCount);
  const [isLoading, setIsLoading] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Reset visible count when machines array changes
  useEffect(() => {
    setVisibleCount(initialLoadCount);
  }, [machines.length, initialLoadCount]);

  // Get currently visible machines
  const visibleMachines = useMemo(() => {
    return machines.slice(0, visibleCount);
  }, [machines, visibleCount]);

  const hasMore = visibleCount < machines.length;

  // Load more machines
  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    // Simulate network delay for smooth UX
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + loadMoreCount, machines.length));
      setIsLoading(false);
    }, 100);
  }, [isLoading, hasMore, loadMoreCount, machines.length]);

  // Intersection Observer for auto-loading when scrolling near bottom
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, isLoading]);

  if (machines.length === 0) {
    return (
      <div className={styles.lazyMachineGrid.emptyState.container}>
        <div className={styles.lazyMachineGrid.emptyState.icon}>⚙️</div>
        <h3 className={styles.lazyMachineGrid.emptyState.title}>{t('noMachines') || 'No machines found'}</h3>
        <p className={styles.lazyMachineGrid.emptyState.description}>{t('noMachinesDescription') || 'No machines match the current filters.'}</p>
      </div>
    );
  }

  return (
    <div className={`${styles.lazyMachineGrid.container} ${className}`}>
      {/* Results summary */}
      <div className={cx('flex items-center justify-between text-sm mb-6', themeClasses.textSecondary)}>
        <span className={themeClasses.textSecondary}>
          {t('showingMachines', {
            visible: visibleMachines.length,
            total: machines.length
          }) || `Showing ${visibleMachines.length} of ${machines.length} machines`}
        </span>
        {machines.length > initialLoadCount && (
          <span className={cx('text-xs px-2 py-1 rounded', themeClasses.bgInfoSubtle, themeClasses.textInfo)}>
            ⚡ Lazy Loading Active
          </span>
        )}
      </div>

      {/* Machine grid */}
      <div className={styles.lazyMachineGrid.grid}>
        {visibleMachines.map((machine, index) => {
          const machineComponents = [
            machine.maschinennummer,
            machine.maschineBK,
            machine.machineName,
            machine.machineId
          ].filter(Boolean);
          
          const machineIdentifier = machineComponents.length > 0 
            ? machineComponents.join('-').replace(/\s/g, '-')
            : `fallback-machine`;
          
          const stableHash = (machineIdentifier + index).replace(/[^a-zA-Z0-9-]/g, '');
          const uniqueKey = `lazy-machine-${stableHash}-${index}`;
          
          return (
            <MachineCard
              key={uniqueKey}
              machine={machine}
              onClick={onMachineClick}
            />
          );
        })}
      </div>

      {/* Load more section */}
      {hasMore && (
        <div ref={loadMoreRef} className={styles.lazyMachineGrid.loadMore.container}>
          {isLoading ? (
            <div className={styles.lazyMachineGrid.loadMore.loading.container}>
              <div className={styles.lazyMachineGrid.loadMore.loading.spinner}></div>
              <span className={styles.lazyMachineGrid.loadMore.loading.text}>{t('loadingMoreMachines') || 'Loading more machines...'}</span>
            </div>
          ) : (
            <div className={styles.lazyMachineGrid.loadMore.manual.container}>
              <button
                onClick={loadMore}
                className={styles.lazyMachineGrid.loadMore.manual.button}
              >
                {t('loadMore', { count: Math.min(loadMoreCount, machines.length - visibleCount) }) || 
                 `Load ${Math.min(loadMoreCount, machines.length - visibleCount)} more machines`}
              </button>
              <div className={styles.lazyMachineGrid.loadMore.manual.hint}>
                {t('autoLoadInfo') || 'Or scroll down to auto-load more'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance info */}
      {machines.length > 50 && (
        <div className={cx(styles.lazyMachineGrid.performance.info, themeClasses.textMuted)}>
          ⚡ <strong>Performance optimized:</strong> Loading {visibleMachines.length} of {machines.length} machines 
          ({((visibleMachines.length / machines.length) * 100).toFixed(1)}% loaded)
        </div>
      )}
    </div>
  );
});

LazyMachineGrid.displayName = 'LazyMachineGrid';

export default LazyMachineGrid;
